import React from 'react';
import * as Sentry from '@sentry/react';
import { isChunkLoadError } from './isChunkLoadError';
import { requestChunkReload } from '../lib/chunkReload';

// Se a aba ficou aberta durante um deploy, o `import()` de uma rota
// lazy-loaded pode apontar pra um arquivo com hash antigo que não existe
// mais no servidor. Normalmente o listener de `vite:preloadError` em main.tsx
// já recarregou a página antes de chegar aqui; este fallback cobre os casos em
// que o erro escapa daquele caminho. O guard compartilhado em chunkReload evita
// que as duas camadas recarreguem em sequência, e também o loop infinito.
const ChunkLoadErrorFallback = ({ resetError }: { resetError?: () => void }) => {
  const [reloading] = React.useState(() => requestChunkReload());

  if (reloading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-500">Atualizando o aplicativo…</p>
      </div>
    );
  }

  // O reload foi barrado pelo guard: recarregar de novo não vai resolver
  // (deploy quebrado, ou o arquivo realmente sumiu). Damos uma saída ao usuário
  // em vez de deixar a tela presa em "Atualizando…".
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6 text-center">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Não foi possível carregar esta tela
        </h2>
        <p className="text-gray-600 mb-4">
          Uma nova versão do aplicativo foi publicada. Recarregue a página; se o
          problema continuar, tente novamente em alguns instantes.
        </p>
        <div className="flex flex-col gap-2">
          <button
            onClick={() => window.location.reload()}
            className="btn-primary w-full"
          >
            Recarregar agora
          </button>
          {resetError && (
            <button
              onClick={resetError}
              className="btn-secondary w-full"
            >
              Tentar novamente
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Componente de fallback para erros
const DefaultErrorFallback = ({ error, resetError }: any) => {
  if (isChunkLoadError(error)) {
    return <ChunkLoadErrorFallback resetError={resetError} />;
  }

  return (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
      <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
        <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
      </div>
      
      <h2 className="text-xl font-semibold text-gray-900 text-center mb-2">
        Oops! Algo deu errado
      </h2>
      
      <p className="text-gray-600 text-center mb-4">
        Ocorreu um erro inesperado. Nossa equipe foi notificada e está trabalhando para resolver o problema.
      </p>
      
      <div className="flex flex-col gap-2">
        <button
          onClick={resetError}
          className="btn-primary w-full"
        >
          Tentar novamente
        </button>
        
        <button
          onClick={() => window.location.href = '/'}
          className="btn-secondary w-full"
        >
          Voltar ao início
        </button>
      </div>
      
      {import.meta.env.DEV && error && (
        <details className="mt-4">
          <summary className="text-sm text-gray-500 cursor-pointer">
            Detalhes do erro (desenvolvimento)
          </summary>
          <pre className="mt-2 text-xs bg-gray-100 p-2 rounded overflow-auto">
            {typeof error === 'object' && error.message ? error.message : String(error)}
          </pre>
        </details>
      )}
    </div>
  </div>
  );
};

// ErrorBoundary simples usando Sentry
export const ErrorBoundary = Sentry.withErrorBoundary(
  ({ children }: { children: React.ReactNode }) => <>{children}</>,
  {
    fallback: DefaultErrorFallback,
    beforeCapture: (scope, error, hint) => {
      scope.setTag('component', 'ErrorBoundary');
      scope.setLevel('error');
      
      if (import.meta.env.DEV) {
        console.error('Error captured by ErrorBoundary:', error);
        console.error('Error hint:', hint);
      }
    },
  }
);

export default ErrorBoundary; 